import { useState, useRef, useEffect, useReducer, useLayoutEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  TextField,
  MenuItem,
  InputAdornment,
  Paper,
  Popper,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { askFlashDiceMessagesReducer } from "../../utils/askFlashDiceMessagesReducer";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { EmptyState } from "../../components/AskFlashDice/EmptyState";
import { MessageList } from "../../components/AskFlashDice/MessageList";
import { InputArea } from "../../components/AskFlashDice/InputArea";
import { ChatHistorySidebar } from "../../components/AskFlashDice/ChatHistorySidebar";
import { useAskFlashDiceStreaming } from "../../hooks/useAskFlashDiceStreaming";
import { useAskFlashDiceChat } from "../../hooks/useAskFlashDiceChat";
import { useAuth } from "../../hooks/useAuth";
import { extractLastSqlFromMarkdown } from "../../utils/markdownParser";
import { SqlResultsProvider, useSqlResults } from "../../contexts/SqlResultsContext";
import ResultsPanel from "../../components/ResultsPanel/ResultsPanel";
import { ROUTES } from "../../constants";
import { Connection } from "../../components/AskFlashDice/Connection";
import { useAiModelProviders } from "../../hooks/useAiModelProviders";
import { groupChatHistoriesByDate, formatChatTimestamp } from "../../utils/chatHistoryHelper";
import { StorageHelper } from "../../utils/storageHelper";

const AskFlashDiceContent = () => {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.userDetails?.user_id;
  const selectedUserDbId = StorageHelper.get("selected_user_db_id");
  const {
    createChat,
    addMessage,
    updateMessage,
    deleteMessage,
    fetchChat,
    deleteChat,
    refetchChats,
    chatList: chatListFromHook,
    chatsLoading: chatsLoadingFromHook,
    hasMoreConversations,
    loadMoreConversations,
    loadingMore: loadingMoreFromHook,
    chatLoading,
    fetchConversationsByKeyword,
    searchByKeywordLoading,
  } = useAskFlashDiceChat(userId ?? undefined);
  const [messages, dispatchMessages] = useReducer(askFlashDiceMessagesReducer, []);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState(null);
  const [currentChatId, setCurrentChatId] = useState(null);
  const chatList = chatListFromHook ?? [];
  const chatsLoading = chatsLoadingFromHook ?? false;
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const autoScrollEnabledRef = useRef(true);
  const backendChatIdRef = useRef(null);
  const loadedSessionIdRef = useRef(null);
  const exampleClickScheduledRef = useRef(false);
  const sendInProgressRef = useRef(false);
  const activeModelRef = useRef(null);
  const { setSqlResults, clearResults } = useSqlResults();

  const generateSessionUuid = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
  const [sessionId, setSessionId] = useState(() => generateSessionUuid());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingSelectedChat, setIsLoadingSelectedChat] = useState(false);
  const [conversationSidebarOpen, setConversationSidebarOpen] = useState(true);
  const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
  const [modelOptions, setModelOptions] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [forceProviderSetup, setForceProviderSetup] = useState(false);
  const { listAiModels } = useAiModelProviders();

  const searchAnchorRef = useRef(null);
  const searchScrollRef = useRef(null);
  const searchLoadMoreRef = useRef(false);
  const searchDebounceRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);

  const loadChatList = () => {
    if (userId != null) refetchChats?.();
  };

  const activeChatTitle =
    chatList.find((c) => c.chat_id === currentChatId)?.title || "Ask-FlashDice-Query";

  // Load conversation when URL has sessionId (open link or switched from sidebar)
  useEffect(() => {
    if (!urlSessionId) {
      loadedSessionIdRef.current = null;
      return;
    }
    if (loadedSessionIdRef.current === urlSessionId) return;
    setSessionId(urlSessionId);
    setIsLoadingSelectedChat(true);
    dispatchMessages({ type: "RESET" });
    setCurrentChatId(null);
    backendChatIdRef.current = null;
    let cancelled = false;
    fetchChat(urlSessionId)
      .then((fullChat) => {
        if (cancelled) return;
        loadedSessionIdRef.current = urlSessionId;
        if (fullChat) {
          backendChatIdRef.current = fullChat.nlp_conversation_id ?? fullChat.chat_id ?? null;
          setCurrentChatId(fullChat.nlp_conversation_id ?? fullChat.chat_id ?? null);
          if (fullChat?.messages?.length) {
            fullChat.messages.forEach((msg, i) => {
              dispatchMessages({
                type: "ADD_MESSAGE",
                message: {
                  id: msg.nlp_message_id != null ? String(msg.nlp_message_id) : `msg_${i}`,
                  nlp_message_id: msg.nlp_message_id ?? null,
                  role: msg.role || "user",
                  content: msg.content || "",
                  model_id: msg.model_id ?? null,
                  feedback: msg.feedback ?? null,
                },
              });
            });
          }
        }
      })
      .catch((err) => {
        if (!cancelled) console.warn("Failed to load chat from URL:", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSelectedChat(false);
      });
    return () => { cancelled = true; };
  }, [urlSessionId]);

  const refreshModels = async () => {
    const uid = user?.userDetails?.user_id;
    if (!uid) return;

    const result = await listAiModels(uid);

    if (result.success && Array.isArray(result.data) && result.data.length) {
      const models = result.data;
      setModelOptions(models);

      setSelectedModel((prev) => {
        // 1) If a default model is flagged, always prefer it.
        const defaultModel = models.find((m) => m.is_default);
        if (defaultModel) {
          return defaultModel.value;
        }
        // 2) Otherwise, if previous selection is still available, keep it.
        if (prev && models.some((m) => m.value === prev)) {
          return prev;
        }
        // 3) Fallback: first model in the list.
        return models[0].value;
      });
      setError(null);
      setForceProviderSetup(false);
    } else if (result.success && Array.isArray(result.data) && !result.data.length) {
      // No models configured → prompt user to add at least one provider.
      setModelOptions([]);
      setSelectedModel("");
      setIsConnectionDialogOpen(true);
      setError(null);
      setForceProviderSetup(true);
    } else if (!result.success && result.error) {
      setError(result.error);
    }
  };

  // Load available AI models for current user (once per user id)
  useEffect(() => {
    if (!user?.userDetails?.user_id) return;
    refreshModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userDetails?.user_id]);


  const handleStreamComplete = (assistantMessageId, content) => {
    const nlpConversationId = backendChatIdRef.current;
    const modelUsed = activeModelRef.current;
    if (nlpConversationId != null && content) {
      const generated_sql = extractLastSqlFromMarkdown(content);
      addMessage({
        nlp_conversation_id: nlpConversationId,
        role: "assistant",
        content,
        generated_sql: generated_sql || undefined,
        model_id: modelUsed || undefined,
      })
        .then((saved) => {
          if (saved?.nlp_message_id != null) {
            dispatchMessages({
              type: "SET_MESSAGE_NLP_ID",
              messageId: assistantMessageId,
              nlp_message_id: saved.nlp_message_id,
              model_id: saved.model_id ?? modelUsed ?? null,
            });
          }
        })
        .catch((err) => console.warn("Failed to persist assistant message:", err));
    }
  };

  const { handleStreaming } = useAskFlashDiceStreaming(
    dispatchMessages,
    setCurrentStreamingMessageId,
    setSqlResults,
    handleStreamComplete
  );

  const scrollToBottom = (behavior = "auto") => {
    const el = scrollContainerRef.current;
    if (el?.scrollTo) {
      el.scrollTo({ top: el.scrollHeight, behavior });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  };

  const scheduleScrollToBottom = (behavior = "auto") => {
    // Two rAFs helps when heavy children (tables/CodeMirror) expand after paint.
    requestAnimationFrame(() => {
      scrollToBottom(behavior);
      requestAnimationFrame(() => scrollToBottom(behavior));
    });
  };

  // Track whether user is near the bottom; only auto-scroll when they are.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const computeNearBottom = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      autoScrollEnabledRef.current = distance < 160;
    };

    computeNearBottom();
    el.addEventListener("scroll", computeNearBottom, { passive: true });
    return () => el.removeEventListener("scroll", computeNearBottom);
  }, []);

  // Keep pinned to bottom while streaming + when new messages arrive (if user was at bottom).
  useLayoutEffect(() => {
    if (!autoScrollEnabledRef.current && !isLoading) return;
    scheduleScrollToBottom(isLoading ? "auto" : "smooth");
  }, [messages, isLoading]);

  // During streaming, DOM can change without `messages` changing (tables/editors finish mounting).
  useEffect(() => {
    if (!isLoading) return;
    const el = scrollContainerRef.current;
    if (!el) return;

    let rafId = null;
    const mo = new MutationObserver(() => {
      if (!autoScrollEnabledRef.current) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => scrollToBottom("auto"));
    });

    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      mo.disconnect();
    };
  }, [isLoading]);

  // Allow example/question chip clicks again only after loading finishes
  useEffect(() => {
    if (!isLoading) {
      exampleClickScheduledRef.current = false;
      sendInProgressRef.current = false;
    }
  }, [isLoading]);

  const runSearchByKeyword = useCallback(
    async (offset = 0, append = false) => {
      if (userId == null) return;
      const keyword = searchKeyword.trim() || null;
      if (offset === 0) setSearchLoadingMore(false);
      else searchLoadMoreRef.current = true;
      try {
        const { conversations: next, hasMore } = await fetchConversationsByKeyword(
          userId,
          keyword,
          10,
          offset
        );
        if (append) {
          setSearchResults((prev) => (offset === 0 ? next : [...prev, ...next]));
        } else {
          setSearchResults(next);
        }
        setSearchHasMore(hasMore);
      } catch (err) {
        console.warn("Search conversations error:", err);
        if (!append) setSearchResults([]);
        setSearchHasMore(false);
      } finally {
        setSearchLoadingMore(false);
        searchLoadMoreRef.current = false;
      }
    },
    [userId, searchKeyword, fetchConversationsByKeyword]
  );

  useEffect(() => {
    if (!searchOpen || userId == null) return;
    runSearchByKeyword(0, false);
  }, [searchOpen, userId]);

  useEffect(() => {
    if (!searchOpen) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      runSearchByKeyword(0, false);
      searchDebounceRef.current = null;
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchKeyword]);

  const handleSearchFocus = () => setSearchOpen(true);
  const handleSearchBlur = () => {
    setTimeout(() => setSearchOpen(false), 200);
  };
  const handleSearchScroll = useCallback(() => {
    const el = searchScrollRef.current;
    if (
      !el ||
      !searchHasMore ||
      searchByKeywordLoading ||
      searchLoadingMore ||
      searchLoadMoreRef.current
    )
      return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      searchLoadMoreRef.current = true;
      setSearchLoadingMore(true);
      runSearchByKeyword(searchResults.length, true);
    }
  }, [
    searchHasMore,
    searchByKeywordLoading,
    searchLoadingMore,
    searchResults.length,
    runSearchByKeyword,
  ]);

  const handleSelectSearchChat = (chat) => {
    setSearchOpen(false);
    setSearchKeyword("");
    if (chat?.session_id) {
      navigate(`${ROUTES.ASK_FLASHDICE}/${encodeURIComponent(chat.session_id)}`, {
        replace: true,
      });
    } else if (chat?.id) {
      setCurrentChatId(chat.id);
      loadedSessionIdRef.current = null;
      dispatchMessages({ type: "RESET" });
    }
  };

  const handleRetry = async (question, assistantMessageId, nlpMessageId, modelId) => {
    const modelToUse = modelId ?? selectedModel;
    if (!question?.trim() || isLoading || !modelToUse) return;
    const nextMessages = messages.filter((m) => m.id !== assistantMessageId);
    if (nlpMessageId != null) {
      try {
        await deleteMessage({ nlp_message_id: nlpMessageId });
      } catch (err) {
        console.warn("Failed to delete message from DB on retry:", err);
      }
    }
    dispatchMessages({ type: "REMOVE_MESSAGE", messageId: assistantMessageId });
    activeModelRef.current = modelToUse;
    setIsLoading(true);
    setError(null);
    try {
      const apiMessages = nextMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      await handleStreaming(question.trim(), apiMessages, sessionId, modelToUse, true);
    } catch (err) {
      console.error("Error on retry:", err);
      setError(err.message || "Failed to get response from AI assistant");
      dispatchMessages({
        type: "ADD_MESSAGE",
        message: {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: `Error: ${err.message || "Failed to get response"}`,
        },
      });
    } finally {
      setIsLoading(false);
      setCurrentStreamingMessageId(null);
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading || sendInProgressRef.current) return;

    sendInProgressRef.current = true;

    // Enforce model selection when sending a message
    if (!selectedModel) {
      setError("Please select a model before sending a question.");
      sendInProgressRef.current = false;
      return;
    }

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
    };
    const userInput = input.trim();

    let chatId = currentChatId;
    if (!chatId) {
      const title = userInput.length > 50 ? userInput.substring(0, 50) + "..." : userInput;
      try {
        const newChat = await createChat({
          user_id: userId,
          user_db_id: selectedUserDbId,
          session_id: sessionId,
          title,
          messages: [{ role: "user", content: userInput, model_id: selectedModel || undefined }],
        });
        if (newChat?.chat_id != null) {
          chatId = newChat.chat_id;
          backendChatIdRef.current = chatId;
          setCurrentChatId(chatId);
          loadedSessionIdRef.current = sessionId;
          navigate(`${ROUTES.ASK_FLASHDICE}/${encodeURIComponent(sessionId)}`, { replace: true });
        }
      } catch (err) {
        console.warn("Failed to persist chat:", err);
      }
    } else {
      addMessage({
        nlp_conversation_id: chatId,
        role: "user",
        content: userInput,
        model_id: selectedModel || undefined,
      }).catch((err) => console.warn("Failed to persist user message:", err));
    }

    dispatchMessages({ type: "ADD_MESSAGE", message: userMessage });
    setInput("");
    activeModelRef.current = selectedModel;
    setIsLoading(true);
    setError(null);

    try {
      const apiMessages = [
        ...messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
        { role: "user", content: userInput },
      ];
      await handleStreaming(userInput, apiMessages, sessionId, selectedModel);
    } catch (err) {
      console.error("Error calling chat API:", err);
      const errorMessage = err.message || "Failed to get response from AI assistant";
      setError(errorMessage);
      dispatchMessages({
        type: "ADD_MESSAGE",
        message: {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: `Error: ${errorMessage}`,
        },
      });
    } finally {
      setIsLoading(false);
      setCurrentStreamingMessageId(null);
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  const handleExampleClick = (example) => {
    if (isLoading || exampleClickScheduledRef.current) return;
    exampleClickScheduledRef.current = true;
    setInput(example);
    setTimeout(() => handleSend(new Event("submit")), 100);
  };

  const handleQuestionClick = (question) => {
    if (isLoading || exampleClickScheduledRef.current) return;
    exampleClickScheduledRef.current = true;
    setInput(question);
    setTimeout(() => handleSend(new Event("submit")), 100);
  };

  const handleNewChat = () => {
    loadedSessionIdRef.current = null;
    navigate(ROUTES.ASK_FLASHDICE, { replace: true });
    setSessionId(generateSessionUuid());
    backendChatIdRef.current = null;
    dispatchMessages({ type: "RESET" });
    setCurrentChatId(null);
    setInput("");
    setError(null);
    setCurrentStreamingMessageId(null);
    clearResults();
  };

  const handleChatSelect = (chat) => {
    const sessionIdToLoad = chat.session_id ?? null;
    setInput("");
    setError(null);
    setCurrentStreamingMessageId(null);
    clearResults();
    if (sessionIdToLoad) {
      navigate(`${ROUTES.ASK_FLASHDICE}/${encodeURIComponent(sessionIdToLoad)}`, { replace: true });
    } else {
      setCurrentChatId(chat.id);
      loadedSessionIdRef.current = null;
      dispatchMessages({ type: "RESET" });
    }
  };

  const handleChatDeleteRequest = (chat) => {
    setDeleteTarget(chat);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deleteChat({ id: deleteTarget.id, chat_id: deleteTarget.id });
      if (result?.success) {
        await loadChatList();
        if (deleteTarget.id === currentChatId) {
          handleNewChat();
        }
      }
    } catch (err) {
      console.warn("Failed to delete chat:", err);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 48px - 24px)",
        display: "flex",
        flexDirection: "row",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Chat History Sidebar - list from DB (NLP conversations) */}
      <Box
        sx={{
          width: conversationSidebarOpen ? 250 : 0,
          flexShrink: 0,
          overflow: "hidden",
          transition: "width 0.2s ease",
        }}
      >
        <ChatHistorySidebar
          chats={chatList.map((c) => ({
            id: c.chat_id,
            title: c.title || "New Chat",
            timestamp: c.created_at || c.chat_timestamp,
            session_id: c.session_id ?? null,
          }))}
          loading={chatsLoading}
          currentChatId={currentChatId}
          onChatSelect={handleChatSelect}
          onNewChat={handleNewChat}
          onChatDelete={handleChatDeleteRequest}
          onLoadMore={loadMoreConversations}
          hasMore={hasMoreConversations}
          loadingMore={loadingMoreFromHook ?? false}
          onOpenConnection={() => setIsConnectionDialogOpen(true)}
        />
      </Box>

      {/* Collapse / expand button - middle of conversation section */}
      <Box
        sx={{
          width: 28,
          flexShrink: 0,
          minHeight: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Tooltip title={conversationSidebarOpen ? "Collapse conversation list" : "Expand conversation list"}>
          <IconButton
            size="small"
            onClick={() => setConversationSidebarOpen((o) => !o)}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#3F49A7",
              },
            }}
          >
            {conversationSidebarOpen ? (
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Delete chat confirmation - existing ConfirmationModal */}
      <ConfirmationModal
        open={deleteDialogOpen}
        title="Delete chat"
        message={`Are you sure you want to delete "${deleteTarget?.title || "New Chat"}"? This action cannot be undone.`}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        buttonText={isDeleting ? "Deleting..." : "Delete"}
        colorCode="#d32f2f"
      />

      {/* Main Chat Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            py: 0.6,
            minHeight: 36,
            borderBottom: "1px solid #e2e8f0",
            width: "100%",
          }}
        >
          {modelOptions.length > 0 && (
            <TextField
              select
              size="small"
              variant="outlined"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (!value) return "Select model";
                  const found = modelOptions.find((m) => m.value === value);
                  const label = found?.label || value;
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#22c55e",
                          flexShrink: 0,
                          boxShadow: "0 0 0 0 rgba(34,197,94,0.45)",
                          animation: "pulseDot 3.4s ease-out infinite",
                          "@keyframes pulseDot": {
                            "0%": {
                              boxShadow: "0 0 0 0 rgba(34,197,94,0.45)",
                            },
                            "70%": {
                              boxShadow: "0 0 0 4px rgba(34,197,94,0)",
                            },
                            "100%": {
                              boxShadow: "0 0 0 0 rgba(34,197,94,0)",
                            },
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {label}
                      </Typography>
                    </Box>
                  );
                },
              }}
              sx={{
                minWidth: 200,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  backgroundColor: "#ffffff",
                  fontSize: "0.8rem",
                  "& fieldset": {
                    borderColor: "#e2e8f0",
                    borderWidth: 1,
                  },
                  "&:hover fieldset": {
                    borderColor: "#cbd5e1",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3F49A7", // brand primary
                    borderWidth: 1,
                  },
                },
              }}
              inputProps={{
                sx: { py: 0.5, px: 1.25 },
              }}
            >
              {modelOptions.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {m.label || m.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "#64748b",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {m.value.split("/")[0] || ""}
                      </Typography>
                    </Box>
                    {selectedModel === m.value && (
                      <CheckIcon
                        sx={{
                          fontSize: 16,
                          color: "#22c55e",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          )}
          <Box ref={searchAnchorRef} sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}>
            <Box sx={{ position: "relative" }}>
              <TextField
                size="small"
                placeholder="Search questions..."
                variant="outlined"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                sx={{
                  width: 200,
                  maxWidth: "100%",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.8125rem",
                    height: 28,
                    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3F49A7",
                      borderWidth: 1.5,
                      boxShadow: "0 0 0 3px rgba(63, 73, 167, 0.12)",
                    },
                  },
                  "& .MuiInputBase-input": {
                    py: 0.5,
                    px: 1.25,
                    "&::placeholder": {
                      color: "#94a3b8",
                      opacity: 1,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 0.25 }}>
                      <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Popper
                open={searchOpen && userId != null}
                anchorEl={searchAnchorRef.current}
                placement="bottom-end"
                style={{ zIndex: 1300 }}
                modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
              >
                <Paper
                  ref={searchScrollRef}
                  onScroll={handleSearchScroll}
                  elevation={0}
                  sx={{
                    width: 280,
                    maxHeight: 340,
                    overflowY: "auto",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-track": {
                      background: "#f8fafc",
                      borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#cbd5e1",
                      borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#94a3b8",
                    },
                  }}
                >
                  {searchByKeywordLoading && !searchResults.length ? (
                    <Box sx={{ px: 2, py: 1.5 }}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Box key={i} sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.5 }}>
                          <Skeleton
                            variant="text"
                            width={i % 2 === 0 ? "90%" : "70%"}
                            height={14}
                            animation="wave"
                            sx={{ bgcolor: "grey.200", borderRadius: 1 }}
                          />
                          <Skeleton
                            variant="text"
                            width="45%"
                            height={10}
                            animation="wave"
                            sx={{ bgcolor: "grey.100", borderRadius: 1 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : !searchResults.length ? (
                    <Box sx={{ p: 2.5, textAlign: "center" }}>
                      <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                        No conversations found.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ py: 0.75 }}>
                      {(() => {
                        const withTimestamp = searchResults.map((c) => ({
                          ...c,
                          timestamp: c.created_at ?? c.created_on,
                        }));
                        const grouped = groupChatHistoriesByDate(withTimestamp);
                        const renderGroup = (title, chats) => {
                          if (!chats?.length) return null;
                          return (
                            <Box key={title} sx={{ mb: 0.75 }}>
                              <Typography
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                  px: 2,
                                  py: 0.5,
                                }}
                              >
                                {title}
                              </Typography>
                              <List dense disablePadding>
                                {chats.map((chat) => (
                                  <ListItemButton
                                    key={chat.id}
                                    onClick={() => handleSelectSearchChat(chat)}
                                    sx={{
                                      py: 0.5,
                                      px: 2,
                                      minHeight: 36,
                                      mx: 0.75,
                                      borderRadius: 1,
                                      flexDirection: "column",
                                      alignItems: "stretch",
                                      transition: "background-color 0.15s ease",
                                      "&:hover": { backgroundColor: "#f1f5f9" },
                                      "&:active": { backgroundColor: "#e2e8f0" },
                                    }}
                                  >
                                    <ListItemText
                                      disableTypography
                                      primary={
                                        <Typography
                                          sx={{
                                            fontSize: "0.8125rem",
                                            fontWeight: 400,
                                            color: "#1e293b",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          {chat.title || "New Chat"}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography
                                          sx={{
                                            fontSize: "0.7rem",
                                            color: "#94a3b8",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          {formatChatTimestamp(chat.timestamp)}
                                        </Typography>
                                      }
                                      sx={{ flex: 1, minWidth: 0 }}
                                    />
                                  </ListItemButton>
                                ))}
                              </List>
                            </Box>
                          );
                        };
                        return (
                          <>
                            {renderGroup("Today", grouped.today)}
                            {renderGroup("Yesterday", grouped.yesterday)}
                            {renderGroup("Previous 7 days", grouped.week)}
                            {renderGroup("Older", grouped.older)}
                            {searchLoadingMore && (
                              <Box sx={{ px: 2, pt: 0.75, pb: 0.5 }}>
                                {[1, 2, 3].map((i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                      mb: 1.25,
                                    }}
                                  >
                                    <Skeleton
                                      variant="text"
                                      width={i % 2 === 0 ? "85%" : "65%"}
                                      height={14}
                                      animation="wave"
                                      sx={{ bgcolor: "grey.200", borderRadius: 1 }}
                                    />
                                    <Skeleton
                                      variant="text"
                                      width="40%"
                                      height={10}
                                      animation="wave"
                                      sx={{ bgcolor: "grey.100", borderRadius: 1 }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Paper>
              </Popper>
            </Box>
            <Tooltip title="Configure AI model providers">
            <IconButton
              size="small"
              onClick={() => setIsConnectionDialogOpen(true)}
              sx={{
                color: "#64748b",
                "&:hover": {
                  color: "#0f172a",
                  backgroundColor: "#e5edff",
                },
              }}
            >
              <MoreVertIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#cbd5e1",
              borderRadius: "3px",
              transition: "background 0.2s ease",
              "&:hover": {
                background: "#94a3b8",
              },
            },
          }}
          ref={scrollContainerRef}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              px: 1.5,
              py: 1,
              minHeight: "100%",
            }}
          >
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                mb: 1.5,
                borderRadius: 2,
                py: 1,
                px: 1.5,
                fontSize: "0.8125rem",
                backgroundColor: "#fef2f2",
                border: "1.5px solid #fecaca",
                boxShadow: "0 1px 3px rgba(220, 38, 38, 0.1)",
                "& .MuiAlert-icon": {
                  color: "#dc2626",
                },
              }}
            >
              {error}
            </Alert>
          )}

          {(isLoadingSelectedChat || (chatLoading && currentChatId)) ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 4,
              }}
            >
              <CircularProgress size={32} sx={{ color: "rgb(44, 52, 123)" }} />
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8125rem" }}>
                Loading conversation…
              </Typography>
            </Box>
          ) : (
            <>
              {messages.length === 0 && (
                <EmptyState
                  onExampleClick={handleExampleClick}
                  isLoading={isLoading}
                />
              )}

              <MessageList
                messages={messages}
                currentStreamingMessageId={currentStreamingMessageId}
                onQuestionClick={handleQuestionClick}
                onRetry={handleRetry}
                modelOptions={modelOptions}
                selectedModel={selectedModel}
                isLoading={isLoading}
                onUpdateMessageFeedback={updateMessage}
                dispatchMessages={dispatchMessages}
              />
            </>
          )}

          <div ref={messagesEndRef} />
          </Box>
        </Box>

        <InputArea
          input={input}
          setInput={setInput}
          isLoading={isLoading}
          onSubmit={handleSend}
        />
      </Box>

      {/* Results Panel */}
      <ResultsPanel tabName={activeChatTitle} />

      <Dialog
        open={isConnectionDialogOpen}
        onClose={(_, reason) => {
          // Prevent closing via backdrop click or ESC; only explicit actions (X button, etc.)
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setIsConnectionDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Connection
          onClose={() => setIsConnectionDialogOpen(false)}
          disableCancel={forceProviderSetup}
          onModelsRefresh={refreshModels}
          onSaved={async () => {
            await refreshModels();
            setIsConnectionDialogOpen(false);
          }}
        />
      </Dialog>
    </Box>
  );
};

export const AskFlashDice = () => {
  return (
    <SqlResultsProvider>
      <AskFlashDiceContent />
    </SqlResultsProvider>
  );
};
